import { Injectable } from '@angular/core';

import {
    defaultPersonalUserPreferences,
    PersonalUserPreferences,
    UserInfo,
    UserSession,
} from 'app/shared/models';
import { StorageService } from 'app/shared/services';

const employeeNumberKey = 'employeeNumber';
const pinCodeKey = 'pin-code';
const userSessionHistoryKey = 'user-session-history';
const startedOnKey = 'user-session-started-on';
const userInfoKey = 'user-info';
const personalUserPreferencesKey = 'personal-user-preferences';
// TODO: remove outdated userSessionDataKey in later Activity App versions
const userSessionDataKey = 'user-session-data';

@Injectable({ providedIn: 'root' })
export class UserSessionStorageService {
    constructor(private storage: StorageService) {}

    getUserSessionFromStorage(): UserSession | undefined {
        const employeeNumber = this.storage.get<number>(employeeNumberKey);
        const pin = this.storage.get<string>(pinCodeKey);
        const startedOn = this.storage.get<number>(startedOnKey) ?? 0;
        const userInfo = this.storage.get<UserInfo>(userInfoKey);
        const data = this.getPersonalUserPreferencesFromStorageOrDefault();
        return !!employeeNumber && !!pin
            ? {
                  credentials: {
                      employeeNumber,
                      pin,
                  },
                  data,
                  startedOn,
                  userInfo,
              }
            : undefined;
    }

    getPersonalUserPreferencesFromStorageOrDefault() {
        const fetched = this.updateAndGetPersonalUserPreferencesKeyData();
        return { ...defaultPersonalUserPreferences, ...fetched };
    }

    updateAndGetPersonalUserPreferencesKeyData() {
        // Check if old key is being used to store user preference data
        // If found, store all data to new personalUserPreferencesKey and clear old key
        // Return data from new key
        const fetchedOldKeyData = this.storage.get<Partial<PersonalUserPreferences>>(userSessionDataKey);
        if (fetchedOldKeyData) {
            this.storage.store(personalUserPreferencesKey, fetchedOldKeyData);
            this.storage.clear(userSessionDataKey);
        }
        return this.storage.get<Partial<PersonalUserPreferences>>(personalUserPreferencesKey);
    }

    putUserSessionToStorage(userSession: UserSession) {
        this.storage.store(employeeNumberKey, userSession.credentials.employeeNumber);
        this.storage.store(pinCodeKey, userSession.credentials.pin);
        this.storage.store(startedOnKey, userSession.startedOn);
        this.storage.store(userInfoKey, userSession.userInfo);
        this.putUserPreferencesToStorage(userSession.data);
    }

    putUserPreferencesToStorage(personalUserPreferences: PersonalUserPreferences) {
        this.storage.store(personalUserPreferencesKey, personalUserPreferences);
    }

    clearUserSessionFromStorage() {
        this.storage.clear(pinCodeKey);
        this.storage.clear(employeeNumberKey);
        this.storage.clear(userInfoKey);
        this.storage.clear(startedOnKey);
        this.storage.clear(personalUserPreferencesKey);
        // TODO: Remove in later Activity App versions
        this.storage.clear(userSessionDataKey);
    }

    putUserSessionHistoryToStorage(history: UserSession[]) {
        this.storage.store(userSessionHistoryKey, history);
    }

    getUserSessionHistory() {
        return this.storage.get<UserSession[]>(userSessionHistoryKey) ?? [];
    }
}
